/**
 * PostHog Analytics Helper for Cash Forth
 * 
 * Provides clean, minimal instrumentation for tracking user behavior.
 * Events are only sent in production when REACT_APP_POSTHOG_KEY is set.
 */

import posthog from 'posthog-js';

// Initialize PostHog only in production with valid key
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const IS_PRODUCTION = process.env.NODE_ENV === 'production' && POSTHOG_KEY;

let initialized = false;

/**
 * Initialize PostHog - call this once at app startup
 */
export const initAnalytics = () => {
  if (initialized || !IS_PRODUCTION) {
    if (!IS_PRODUCTION) {
      console.log('Analytics: Disabled (not production or missing key)');
    }
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    loaded: (posthog) => {
      if (process.env.NODE_ENV === 'development') {
        posthog.debug();
      }
    },
    // ----------------------------------------------------------------
    // Cookieless / no-banner configuration
    // ----------------------------------------------------------------
    // 'memory' = no cookies, no localStorage, no sessionStorage. The
    // distinct_id lives only in JavaScript memory and regenerates on
    // every page reload. This removes the need for a GDPR/ePrivacy
    // cookie consent banner. Signed-in users are still stitched server
    // side via posthog.identify(clerkUserId).
    persistence: 'memory',
    disable_session_recording: true,
    respect_dnt: true,
    // ----------------------------------------------------------------
    capture_pageview: false, // We'll handle this manually
    capture_pageleave: true,
    autocapture: false, // We want explicit events only
  });

  initialized = true;
  console.log('Analytics: Initialized');
};

/**
 * Identify a user with Clerk data
 */
export const identifyUser = (userId, properties = {}) => {
  if (!IS_PRODUCTION) return;

  posthog.identify(userId, {
    email: properties.email,
    signup_method: properties.signupMethod,
    account_age_days: properties.accountAgeDays,
    total_transactions: properties.totalTransactions,
    total_forecasts_generated: properties.totalForecasts,
    ...properties,
  });
};

/**
 * Reset user identity (on logout)
 */
export const resetUser = () => {
  if (!IS_PRODUCTION) return;
  posthog.reset();
};

/**
 * Core event capture function
 */
const capture = (eventName, properties = {}) => {
  if (!IS_PRODUCTION) {
    console.log(`Analytics Event: ${eventName}`, properties);
    return;
  }
  posthog.capture(eventName, properties);
};

// ============================================
// SESSION STORAGE HELPERS
// ============================================

const SESSION_KEY = 'cashforth_session';

// Session-scoped tracking of users for whom we've already fired
// `account_created`. Held in module memory only — no localStorage —
// to keep the no-cookie-banner posture intact. The risk of a duplicate
// signup event is bounded because trackSignupCompleted is only invoked
// when Clerk's `createdAt` is < 2 minutes old (see App.js).
const _signupTrackedUserIds = new Set();

const getSessionData = () => {
  try {
    const data = sessionStorage.getItem(SESSION_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

const setSessionData = (data) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
};

// ============================================
// A. ACQUISITION & SESSION EVENTS
// ============================================

/**
 * Track when user first views the site
 */
export const trackSiteViewed = () => {
  capture('site_viewed', {
    path: window.location.pathname,
    referrer: document.referrer || 'direct',
  });
};

// ============================================
// LANDING / ACQUISITION FUNNEL EVENTS
// ============================================

/**
 * Track click on a "Learn More" CTA. The landing page has two: one in
 * section 1 (hero), one in section 6 (bottom). The /learn page also
 * exposes a Pricing button which routes back to the landing modal.
 */
export const trackLearnMoreClicked = (location) => {
  capture('learn_more_clicked', { location });
};

/**
 * Track click on a "Try it for Free" / "Start Forecasting" CTA — the
 * primary anonymous → Guest Mode transition.
 */
export const trackTryItFreeClicked = (location) => {
  capture('try_it_free_clicked', { location });
};

/**
 * Track click on the "Pricing" CTA in the landing header.
 */
export const trackPricingClicked = (location) => {
  capture('pricing_clicked', { location });
};

/**
 * Track click on any "Upgrade" / "Save Forecast" CTA — the primary
 * Guest Mode → Stripe Checkout transition. Both labels collapse into
 * a single event because they trigger the same UpgradeModal; the
 * cta_label property lets you separate them in PostHog if you care.
 */
export const trackUpgradeClicked = ({ location, ctaLabel }) => {
  capture('upgrade_clicked', {
    location,
    cta_label: ctaLabel,
  });
};

/**
 * Track session start
 */
export const trackSessionStarted = () => {
  const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  setSessionData({
    id: sessionId,
    startTime: Date.now(),
  });
  
  capture('session_started', {
    session_id: sessionId,
  });

  return sessionId;
};

/**
 * Track session end
 */
export const trackSessionEnded = () => {
  const session = getSessionData();
  if (!session) return;
  
  const durationSeconds = Math.round((Date.now() - session.startTime) / 1000);
  
  capture('session_ended', {
    session_id: session.id,
    duration_seconds: durationSeconds,
  });
};

/**
 * Track account creation
 */
export const trackAccountCreated = (signupMethod) => {
  capture('account_created', {
    signup_method: signupMethod,
  });
};

/**
 * One-time signup tracking: fires "account_created" with full properties
 * and calls posthog.identify() using the backend user ID.
 * Uses an in-memory Set to ensure it runs only once per user per session.
 * Returns true if the event was fired, false if already tracked.
 */
export const trackSignupCompleted = (userId, { email, signupMethod, createdAt }) => {
  if (_signupTrackedUserIds.has(userId)) return false;

  capture('account_created', {
    email,
    signup_method: signupMethod,
    created_at: createdAt,
  });

  if (IS_PRODUCTION) {
    posthog.identify(userId, {
      email,
      created_at: createdAt,
      signup_method: signupMethod,
    });
  } else {
    console.log('Analytics: posthog.identify()', userId, {
      email,
      created_at: createdAt,
      signup_method: signupMethod,
    });
  }

  _signupTrackedUserIds.add(userId);
  return true;
};

// ============================================
// B. MANUAL TRANSACTION WORKFLOW
// ============================================

/**
 * Track transaction creation
 */
export const trackTransactionCreated = (transaction) => {
  const hasCustomAmounts = transaction.custom_amounts && 
    Object.keys(transaction.custom_amounts).length > 0;
  
  capture('transaction_created', {
    type: transaction.is_expense ? 'expense' : 'income',
    amount: Math.abs(transaction.amount || transaction.default_amount || 0),
    recurrence_type: transaction.recurrence_type || 'one_time',
    hypothetical: transaction.is_hypothetical || false,
    customized_future_amounts: hasCustomAmounts,
  });
};

/**
 * Track transaction edit
 */
export const trackTransactionEdited = (transaction, changedFields = []) => {
  const hasCustomAmounts = transaction.custom_amounts && 
    Object.keys(transaction.custom_amounts).length > 0;
  
  capture('transaction_edited', {
    type: transaction.is_expense ? 'expense' : 'income',
    amount: Math.abs(transaction.amount || transaction.default_amount || 0),
    recurrence_type: transaction.recurrence_type || 'one_time',
    hypothetical: transaction.is_hypothetical || false,
    customized_future_amounts: hasCustomAmounts,
    fields_changed: changedFields,
  });
};

// ============================================
// C. CSV IMPORT WORKFLOW
// ============================================

/**
 * Track CSV import started
 */
export const trackCSVImportStarted = (fileName, fileSize, rowCountEstimate = null) => {
  capture('csv_import_started', {
    file_name: fileName,
    file_size: fileSize,
    row_count_estimate: rowCountEstimate,
  });
};

/**
 * Track CSV import completed
 */
export const trackCSVImportCompleted = (totalRows, transactionsDetected) => {
  capture('csv_import_completed', {
    total_rows: totalRows,
    transactions_detected: transactionsDetected,
  });
};

// ============================================
// D. FORECAST EVENTS
// ============================================

/**
 * Track forecast generation
 */
export const trackForecastGenerated = (source, transactionCount, isFirstForecast) => {
  capture('forecast_generated', {
    source: source, // 'manual' or 'csv'
    transaction_count: transactionCount,
    is_first_forecast: isFirstForecast,
  });
};

/**
 * Track forecast update
 */
export const trackForecastUpdated = (transactionCount, updatedFrom) => {
  capture('forecast_updated', {
    transaction_count: transactionCount,
    updated_from: updatedFrom, // 'manual_transaction' or 'csv_import'
  });
};

// ============================================
// E. SETTINGS EVENTS
// ============================================

/**
 * Track starting balance saved
 */
export const trackStartingBalanceSaved = (amount) => {
  capture('starting_balance_saved', {
    starting_balance_amount: amount,
  });
};

/**
 * Track low balance alert set
 */
export const trackLowBalanceAlertSet = (threshold) => {
  capture('low_balance_alert_set', {
    threshold_value: threshold,
  });
};

/**
 * Track high balance alert set
 */
export const trackHighBalanceAlertSet = (threshold) => {
  capture('high_balance_alert_set', {
    threshold_value: threshold,
  });
};

// ============================================
// F. VIEW & INTERACTION EVENTS
// ============================================

/**
 * Track time horizon selection
 */
export const trackTimeHorizonSelected = (days) => {
  const rangeMap = {
    '30': '30d',
    '90': '90d',
    '180': '6mo',
    '365': '1yr',
    '730': '2yr',
  };
  
  capture('time_horizon_selected', {
    range: rangeMap[String(days)] || `${days}d`,
  });
};

/**
 * Track forecast view selection
 */
export const trackForecastViewSelected = (view) => {
  capture('forecast_view_selected', {
    view: view, // 'ledger' or 'chart'
  });
};

// Export posthog instance for advanced usage
export { posthog };
